import React from 'react';
import { User } from 'lucide-react';
import { getProfileStatusRingColor } from '../../lib/placementUtils';

/**
 * The ONE avatar used by every support-ticket surface.
 *
 * Before this existed, each surface had its own bespoke avatar markup, which is
 * how the same person could render as a photo in the list, initials in the chat
 * header and an empty circle in the details panel.
 *
 * Fallback order (fixed contract — do not vary it per surface):
 *   1. photoUrl, if it exists and has not failed to load
 *   2. coloured initials, deterministic per name
 *   3. placeholder icon
 */

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

/** Mirrors the --msg-avatar-* tokens in messaging.css. */
const SIZE_CLASS: Record<AvatarSize, string> = {
  xs: 'h-5 w-5 text-[11px]',
  sm: 'h-7 w-7 text-[10px]',
  md: 'h-8 w-8 text-[11px]',
  lg: 'h-10 w-10 text-sm',
  xl: 'h-14 w-14 text-lg',
};

/** Deterministic palette — the same person keeps the same colour everywhere. */
const AVATAR_TONES = [
  'bg-blue-100 text-blue-700',
  'bg-emerald-100 text-emerald-700',
  'bg-amber-100 text-amber-800',
  'bg-rose-100 text-rose-700',
  'bg-violet-100 text-violet-700',
  'bg-cyan-100 text-cyan-700',
  'bg-orange-100 text-orange-700',
  'bg-teal-100 text-teal-700',
];

export function avatarTone(name?: string): string {
  const key = (name || '').trim().toLowerCase();
  if (!key) return AVATAR_TONES[0];
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) {
    hash = (hash * 31 + key.charCodeAt(i)) % 100000;
  }
  return AVATAR_TONES[hash % AVATAR_TONES.length];
}

export function avatarInitials(name?: string): string {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export interface AvatarProps {
  name?: string;
  photoUrl?: string;
  size?: AvatarSize;
  className?: string;
  /** Override the placeholder icon (e.g. a role glyph). */
  icon?: React.ReactNode;
  /** Thin white ring — used on the active list row and the chat header. */
  ring?: boolean;
  /**
   * Explicit tone class (bg + text) that overrides the name-hash palette.
   * Used where the ROLE carries meaning, e.g. the chat's admin / applicant /
   * internal-note colours.
   */
  tone?: string;
  /**
   * Application object or status ring color override ('red' | 'blue' | 'green' | 'yellow').
   * Renders a single status ring around the avatar image.
   */
  app?: any;
  statusRingColor?: 'red' | 'blue' | 'green' | 'yellow' | null;
}

export const Avatar = React.memo(function Avatar({
  name,
  photoUrl,
  size = 'md',
  className = '',
  icon,
  ring = false,
  tone,
  app,
  statusRingColor,
}: AvatarProps) {
  const [failed, setFailed] = React.useState(false);

  // A NEW url must clear the previous failure. Without this, one broken photo
  // permanently suppresses the next person's photo in a recycled list row —
  // which is exactly what the "wrong/missing avatar" reports looked like.
  React.useEffect(() => {
    setFailed(false);
  }, [photoUrl]);

  const showPhoto = !!photoUrl && !failed;
  const initials = avatarInitials(name);

  // Compute ring color from app prop if provided, otherwise statusRingColor prop
  let derivedRing: 'red' | 'blue' | 'green' | 'yellow' | null = statusRingColor || null;
  if (!derivedRing && app) {
    derivedRing = getProfileStatusRingColor(app);
  }

  const ringStyleClass =
    derivedRing === 'red'
      ? 'ring-2 ring-red-500 ring-offset-1'
      : derivedRing === 'blue'
      ? 'ring-2 ring-blue-500 ring-offset-1'
      : derivedRing === 'green'
      ? 'ring-2 ring-emerald-500 ring-offset-1'
      : derivedRing === 'yellow'
      ? 'ring-2 ring-amber-400 ring-offset-1'
      : ring
      ? 'ring-2 ring-white'
      : '';

  return (
    <span
      className={[
        'inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full font-extrabold select-none transition-all',
        ringStyleClass,
        showPhoto ? 'bg-slate-100' : (tone || avatarTone(name)),
        SIZE_CLASS[size],
        className,
      ].join(' ')}
      title={name || undefined}
    >
      {showPhoto ? (
        <img
          src={photoUrl}
          alt={name ? `${name} avatar` : ''}
          /* object-center matters: without it, non-square source photos anchor
             top-left and read as a "distorted" crop of the wrong region. */
          className="h-full w-full object-cover object-center"
          loading="lazy"
          decoding="async"
          /* Firebase Storage / Google avatar hosts reject some referrer patterns
             and silently return nothing — a real cause of blank avatars. */
          referrerPolicy="no-referrer"
          onError={() => setFailed(true)}
        />
      ) : initials ? (
        <span className="leading-none">{initials}</span>
      ) : icon ? (
        <span className="flex items-center justify-center [&>svg]:h-1/2 [&>svg]:w-1/2">{icon}</span>
      ) : (
        <User className="h-1/2 w-1/2 opacity-70" aria-hidden="true" />
      )}
    </span>
  );
});

Avatar.displayName = 'Avatar';

export default Avatar;
